import { ButtonBase, styled, Tab, Tabs } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef } from 'react';
import Box from 'shell/components/Box';
import Divider from 'shell/components/Divider';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import TabPanel from 'shell/components/TabPanel';
import Text from 'shell/components/Text';

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        width: '100%',
        height: '100%',
        background: theme.palette.ensightful.shades[0],
    };
});

const CustomText = styled(Text)(({ device, theme }) => {
    const width = {
        tablet: '229px',
        desktop: '325px',
    }[device];

    return {
        width,
        position: 'absolute',
        fontSize: 'clamp(0.5rem, 2vw, 1.5rem)',
        lineHeight: 1,
        padding: theme.spacing(5),
    };
});

const CustomIconButton = styled(IconButton)(({ device, theme }) => {
    const left = {
        tablet: `calc(229px - 1.75rem - ${theme.spacing(5 * 2)})`,
        desktop: `calc(325px - 1.75rem - ${theme.spacing(5 * 2)})`,
    }[device];

    return {
        position: 'absolute',
        left,
        margin: theme.spacing(5),
    };
});

const CustomTabs = styled(Tabs)(({ device, label }) => {
    const width = {
        tablet: '229px',
        desktop: '325px',
    }[device];

    return {
        marginTop: label ? '4rem' : '0.5rem',
        width,
        '& .MuiTabs-scroller': {
            overflowY: 'auto !important',
        },
        '& .MuiTabs-flexContainer': {
            padding: '6px 15px',
        },
        '& .MuiTabs-indicator': {
            display: 'none',
        },
    };
});

const CustomTab = styled(Tab)(({ device, $disabled, theme }) => {
    const size = {
        mobile: 'small',
        tablet: 'large',
        desktop: 'large',
    }[device];

    return {
        alignItems: 'flex-start',
        borderRadius: '6px',
        minHeight: 'unset',
        height: '2.5rem',
        padding: 0,
        fontSize: `${theme.typography.p1.fontSize[size]}rem`,
        fontWeight: theme.typography.p1.fontWeight.medium,
        letterSpacing: `${theme.typography.p1.letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight('p1', size)}rem`,
        color: theme.palette.ensightful.neutral[900],
        transition: theme.transitions.default,
        '&.Mui-selected': {
            background: theme.palette.ensightful.primary[50],
            color: theme.palette.ensightful.neutral[900],
        },
        '&&.Mui-focusVisible': {
            background: theme.palette.ensightful.primary[100],
            outline: `${theme.palette.ensightful.secondary[400]} solid 2px !important`,
            outlineOffset: '4px',
        },
        '&:hover': {
            background: theme.palette.ensightful.primary[100],
        },
        '&:active': {
            background: theme.palette.ensightful.primary[200],
        },
        ...($disabled && {
            color: theme.palette.ensightful.neutral[400],
            pointerEvents: 'none',
        }),
    };
});

const CustomBox = styled(Box)(({ index, theme }) => {
    return {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(2),
        width: '100%',
        height: '100%',
        textAlign: 'left',
        ...(index && {
            borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        }),
    };
});

const TabComponentRoot = styled(ButtonBase)(() => {
    return {
        width: '100%',
        height: '100%',
    };
});

const TabComponentInner = styled(Box)(({ index, theme }) => {
    return {
        width: '100%',
        height: '100%',
        ...(index && {
            borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        }),
    };
});

const Panel = ({ className, label, options, renderTab, selected, onAdd, onBeforeChange, onChange, ...rest }) => {
    const { device } = useViewport();

    let tabComponent = null;
    if (renderTab) {
        tabComponent = forwardRef((props, ref) => {
            const index = props.ownerState.value;
            const option = options[index];
            return (
                <TabComponentRoot ref={ref} {...props}>
                    <TabComponentInner index={index}>{renderTab(option)}</TabComponentInner>
                </TabComponentRoot>
            );
        });
    }

    return (
        <CustomStack className={className} direction={'row'}>
            {label && (
                <CustomText id={'panel-label'} component={'p'} variant={'h6'} weight={'semibold'} device={device}>
                    {label}
                </CustomText>
            )}
            {onAdd && (
                <CustomIconButton
                    aria-label={'create'}
                    data-cy={'course-setup-button'}
                    device={device}
                    kind={'add'}
                    size={'small'}
                    variant={'circle'}
                    onClick={onAdd}
                />
            )}
            <CustomTabs
                aria-labelledby={'panel-label'}
                device={device}
                label={label}
                orientation={'vertical'}
                value={options.findIndex(({ key }) => key === selected)}
                onChange={(_, index) => {
                    if (onBeforeChange) {
                        onBeforeChange(() => onChange(options[index].key));
                    } else {
                        onChange(options[index].key);
                    }
                }}
                {...rest}
            >
                {options.map((option, index) => (
                    <CustomTab
                        id={`tab-${option.key}`}
                        key={option.key}
                        aria-controls={`tabpanel-${option.key}`}
                        aria-disabled={option.disabled}
                        device={device}
                        $disabled={option.disabled}
                        label={<CustomBox index={index}>{option.value}</CustomBox>}
                        onKeyDown={(e) => {
                            if (option.disabled) e.preventDefault();
                        }}
                        {...(tabComponent ? { component: tabComponent } : {})}
                    />
                ))}
            </CustomTabs>
            <Divider flexItem orientation={'vertical'} />
            {options.map(({ key, content }) => (
                <TabPanel key={key} direction={'row'} $key={key} selected={selected}>
                    {content}
                </TabPanel>
            ))}
        </CustomStack>
    );
};

export default Panel;
